import { Directive, OnInit, OnDestroy, ChangeDetectorRef, OutputRefSubscription, inject } from '@angular/core';
import { ModalComponent, OffcanvasComponent } from '@coreui/angular';
import { Capacitor } from '@capacitor/core';
import { BackButtonService } from '../services/back-button.service';

/**
 * Directive for integrating Capacitor's native hardware back button
 * with CoreUI's c-modal and c-offcanvas components.
 *
 * Usage: Add `appNativeBack` to any <c-modal id="someId">.
 * Example: <c-modal id="myModal" appNativeBack>
 *
 * Web-safe: exits immediately when not running on a native platform.
 */
@Directive({
    selector: '[appNativeBack]',
    standalone: false
})
export class NativeBackDirective implements OnInit, OnDestroy {
  private subscription?: OutputRefSubscription;
  private ignoreNextClose = false;

  // { self: true } means: look ONLY at this element's own injector (the host c-modal/c-offcanvas),
  // not at parent injectors. This reliably returns the host component instance.
  private readonly modal = inject(ModalComponent, { optional: true, self: true });
  private readonly offcanvas = inject(OffcanvasComponent, { optional: true, self: true });
  private readonly backButtonService = inject(BackButtonService);
  private readonly cdRef = inject(ChangeDetectorRef);

  ngOnInit(): void {
    // Bail out immediately on web — no-op for browser users
    if (!Capacitor.isNativePlatform()) return;

    const target = this.modal ?? this.offcanvas;
    if (!target) return;

    // Subscribe to CoreUI's own visibleChange output on the host component
    this.subscription = target.visibleChange.subscribe((visible: boolean) => {
      if (visible) {
        // Modal just opened: push a back-button handler onto the stack
        this.backButtonService.pushHandler(() => {
          this.ignoreNextClose = true;
          target.visible.set(false);
          this.cdRef.detectChanges();
          return true; // prevents default (app exit / location.back)
        });
      } else {
        // Modal closed
        if (this.ignoreNextClose) {
          // We triggered the close from the handler above — already consumed
          this.ignoreNextClose = false;
        } else {
          // User dismissed via UI (X button, backdrop) — clean up the stack
          this.backButtonService.popHandler();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
